/* ==========================================================================
   LUSHBOW theme.js — small, dependency-free
   - sticky header state
   - mobile nav + search modal
   - announcement rotation (mobile)
   - scroll reveal
   - quantity steppers
   - AJAX add to cart (+ toast, cart count)
   - product variant picker
   - product gallery thumbs
   - cart line updates
   ========================================================================== */
(function () {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const cfg = window.lushbow || { routes: {}, strings: {} };

  /* ---------- Money ---------- */
  function formatMoney(cents, format) {
    const fmt = format || cfg.moneyFormat || '${{amount}}';
    const value = (cents / 100);
    const match = /\{\{\s*(\w+)\s*\}\}/.exec(fmt);
    let out;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': out = Math.round(value).toLocaleString('en-US'); break;
      case 'amount_with_comma_separator': out = value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); break;
      case 'amount_no_decimals_with_comma_separator': out = Math.round(value).toLocaleString('de-DE'); break;
      case 'amount_with_apostrophe_separator': out = value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'"); break;
      default: out = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, out).replace(/<[^>]+>/g, '');
  }

  /* ---------- Toast ---------- */
  let toastTimer;
  function toast(html) {
    const el = $('#Toast');
    if (!el) return;
    el.innerHTML = html;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 3800);
  }

  /* ---------- Header ---------- */
  const header = $('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Mobile nav ---------- */
  const mobileNav = $('[data-mobile-nav]');
  const openBtn = $('[data-mobile-nav-open]');
  function setNav(open) {
    if (!mobileNav) return;
    mobileNav.classList.toggle('is-open', open);
    mobileNav.setAttribute('aria-hidden', String(!open));
    if (openBtn) openBtn.setAttribute('aria-expanded', String(open));
    document.body.style.overflow = open ? 'hidden' : '';
  }
  openBtn && openBtn.addEventListener('click', () => setNav(true));
  $$('[data-mobile-nav-close]').forEach(b => b.addEventListener('click', () => setNav(false)));

  /* ---------- Search modal ---------- */
  const searchModal = $('[data-search-modal]');
  function setSearch(open) {
    if (!searchModal) return;
    searchModal.classList.toggle('is-open', open);
    searchModal.setAttribute('aria-hidden', String(!open));
    document.body.style.overflow = open ? 'hidden' : '';
    if (open) setTimeout(() => { const i = $('[data-search-input]'); i && i.focus(); }, 50);
  }
  $$('[data-search-open]').forEach(b => b.addEventListener('click', () => setSearch(true)));
  $$('[data-search-close]').forEach(b => b.addEventListener('click', () => setSearch(false)));
  searchModal && searchModal.addEventListener('click', e => { if (e.target === searchModal) setSearch(false); });
  document.addEventListener('keydown', e => { if (e.key === 'Escape') { setSearch(false); setNav(false); } });

  /* ---------- Announcement rotation (mobile only) ---------- */
  const ann = $('.announcement[data-rotate="true"]');
  if (ann) {
    const items = $$('.announcement__item', ann);
    if (items.length > 1) {
      let i = 0;
      const mq = window.matchMedia('(max-width: 749px)');
      setInterval(() => {
        if (!mq.matches) return;
        items[i].style.display = 'none';
        i = (i + 1) % items.length;
        items[i].style.display = 'flex';
      }, parseInt(ann.dataset.interval, 10) || 5000);
    }
  }

  /* ---------- Scroll reveal ---------- */
  const reveals = $$('.reveal');
  if ('IntersectionObserver' in window && reveals.length) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(en => { if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    reveals.forEach(el => io.observe(el));
  } else {
    reveals.forEach(el => el.classList.add('is-visible'));
  }

  /* ---------- Quantity steppers ---------- */
  document.addEventListener('click', (e) => {
    const minus = e.target.closest('[data-qty-minus]');
    const plus = e.target.closest('[data-qty-plus]');
    if (!minus && !plus) return;
    const wrap = (minus || plus).closest('[data-qty]');
    const input = $('input', wrap);
    if (!input) return;
    const min = parseInt(input.min || '0', 10);
    let v = parseInt(input.value || '1', 10);
    v = plus ? v + 1 : Math.max(min, v - 1);
    input.value = v;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Cart count ---------- */
  function updateCartCount(count) {
    $$('[data-cart-count]').forEach(el => { el.textContent = count; el.dataset.count = count; el.style.animation = 'none'; void el.offsetWidth; });
  }

  /* ---------- AJAX add to cart ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-ajax-cart-form]');
    if (!form) return;
    e.preventDefault();
    const btn = $('[type="submit"]', form);
    const label = btn ? btn.innerHTML : '';
    if (btn) { btn.disabled = true; btn.innerHTML = '…'; }
    try {
      const fd = new FormData(form);
      const res = await fetch(cfg.routes.cart_add_url + '.js', { method: 'POST', body: fd, headers: { 'Accept': 'application/json' } });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.description || cfg.strings.error); }
      const cart = await fetch(cfg.routes.cart_url + '.js').then(r => r.json());
      updateCartCount(cart.item_count);
      toast(`✓ ${cfg.strings.added} · <a href="${cfg.routes.cart_url}">${cfg.strings.viewCart}</a>`);
      if (btn) { btn.innerHTML = '✓'; setTimeout(() => { btn.innerHTML = label; btn.disabled = false; }, 1400); }
    } catch (err) {
      toast(err.message || cfg.strings.error);
      if (btn) { btn.innerHTML = label; btn.disabled = false; }
    }
  });

  /* ---------- Product variant picker ---------- */
  $$('[data-product-form]').forEach(form => {
    const variantsEl = $('[data-variants]', form);
    if (!variantsEl) return;
    let variants = [];
    try { variants = JSON.parse(variantsEl.textContent); } catch (e) { return; }
    const idInput = $('[data-variant-id]', form);
    const addBtn = $('[data-add-button]', form);
    const addText = $('[data-add-text]', form);
    const priceWrap = document.querySelector('[data-price]');
    const mainImg = document.querySelector('[data-main-image]');

    function selected() {
      const groups = $$('fieldset.variant-group', form);
      return groups.map(g => { const c = $('input:checked', g); return c ? c.value : null; });
    }
    function sync() {
      const opts = selected();
      const v = variants.find(v => opts.every((o, i) => v.options[i] === o));
      if (!v) { addBtn.disabled = true; addText.textContent = cfg.strings.unavailable; return; }
      idInput.value = v.id;
      addBtn.disabled = !v.available;
      addText.textContent = v.available ? cfg.strings.addToCart : cfg.strings.soldOut;
      if (priceWrap) {
        const compare = v.compare_at_price && v.compare_at_price > v.price ? `<s class="price__compare">${formatMoney(v.compare_at_price)}</s>` : '';
        priceWrap.innerHTML = `<div class="price price--lg${compare ? ' price__sale' : ''}"><span class="price__regular">${formatMoney(v.price)}</span>${compare}</div>`;
      }
      if (mainImg && v.featured_image && v.featured_image.src) mainImg.src = v.featured_image.src;
      const url = new URL(window.location.href); url.searchParams.set('variant', v.id); history.replaceState({}, '', url);
    }
    $$('[data-option-input]', form).forEach(i => i.addEventListener('change', sync));
  });

  /* ---------- Gallery thumbs ---------- */
  $$('[data-thumb]').forEach(btn => btn.addEventListener('click', () => {
    const main = document.querySelector('[data-main-image]');
    if (!main) return;
    main.src = btn.dataset.src; main.removeAttribute('srcset');
    $$('[data-thumb]').forEach(b => b.setAttribute('aria-current', 'false'));
    btn.setAttribute('aria-current', 'true');
  }));

  /* ---------- Cart page: line updates ---------- */
  const cartForm = $('[data-cart-form]');
  if (cartForm) {
    let t;
    cartForm.addEventListener('change', (e) => {
      const input = e.target.closest('[data-line-qty]');
      if (!input) return;
      clearTimeout(t);
      t = setTimeout(async () => {
        try {
          const res = await fetch(cfg.routes.cart_change_url + '.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ line: parseInt(input.dataset.line, 10), quantity: parseInt(input.value, 10) })
          });
          if (!res.ok) throw new Error();
          window.location.reload();
        } catch (err) { toast(cfg.strings.error); }
      }, 350);
    });
    $$('[data-remove-line]', cartForm).forEach(a => a.addEventListener('click', async (e) => {
      e.preventDefault();
      try {
        await fetch(cfg.routes.cart_change_url + '.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ line: parseInt(a.dataset.removeLine, 10), quantity: 0 })
        });
        window.location.reload();
      } catch (err) { toast(cfg.strings.error); }
    }));
  }

  /* ---------- Address country default ---------- */
  $$('select[data-default]').forEach(s => { if (s.dataset.default) s.value = s.dataset.default; });
})();
